/**
 * A line's true centre, all along it: a rough path along the line (its skeleton, a pixel or so from
 * the middle) is measured across on the full-resolution plan — the darkness profile's one clean hump
 * gives the centre to a small fraction of a pixel, the line's width and its tone — and the centres
 * are cut into the straight lines and circular arcs the line was drawn with (fit.ts).
 *
 * A corner the path turns (an L, a box's corner) is found on the rough path first: the ink round it
 * is two lines' at once, so nothing is measured there, and the two arms are fitted apart and meet at
 * their true corner — sharp, never rounded off.
 */
import type { Luma } from './ink.ts';
import { lumaAt } from './ink.ts';
import { cutIntoPieces, type Fit } from './fit.ts';

export interface Across {
  off: number; // the line's centre, along the normal from the point (px)
  width: number; // px
  v: number; // darkness
  ok: boolean;
  /** Its edges drawn softly (the pixels its edges pass through part-dark, as a smoothly drawn line's are): 0, 1 or 2. */
  soft: number;
}

const STEP = 0.25;

/**
 * The line through (x, y), measured across along the unit normal (nx, ny): the darkness profile's
 * one clean hump about the point — its centroid, the ink it holds, its tone. Not ok where the ink
 * there is not one clean line.
 */
export function across(L: Luma, lk: number, paperAt: (x: number, y: number) => number, x: number, y: number, nx: number, ny: number, R: number, buf: Float32Array): Across {
  const n = Math.round((2 * R) / STEP) + 1;
  const P = Math.max(40, paperAt(x, y));
  for (let i = 0; i < n; i++) {
    const t = -R + i * STEP;
    const l = lumaAt(L, (x + nx * t) * lk, (y + ny * t) * lk);
    const d = (P - l) / P;
    buf[i] = d < 0 ? 0 : d > 1 ? 1 : d;
  }
  const c = (n - 1) >> 1;
  const reach = Math.round(1.5 / STEP);
  let im = c;
  for (let i = c - reach; i <= c + reach; i++) if (buf[i]! > buf[im]!) im = i;
  const m = buf[im]!;
  if (m < 0.12) return { off: 0, width: 0, v: 0, ok: false, soft: 0 };
  // outward from the peak while the ink is the line's: across its dark middle, then down its edge
  // while it keeps falling away — stopping where it levels off (paper, or a shaded band the line
  // runs along) or climbs again (another line)
  const falling = (a: number, b: number) => buf[b]! < buf[a]! - 0.004;
  let l = im,
    r = im;
  while (l > 0 && (buf[l - 1]! > 0.6 * m ? buf[l - 1]! <= buf[l]! + 0.05 : falling(l, l - 1))) l--;
  while (r < n - 1 && (buf[r + 1]! > 0.6 * m ? buf[r + 1]! <= buf[r]! + 0.05 : falling(r, r + 1))) r++;
  const edge = (l === 0 && buf[0]! > 0.25 * m) || (r === n - 1 && buf[n - 1]! > 0.25 * m);
  // what the line stands on (paper: nothing; a shaded band: its tone), taken off before measuring it
  const b = Math.min(buf[l]!, buf[r]!);
  const mm = m - b;
  if (mm < 0.08) return { off: 0, width: 0, v: 0, ok: false, soft: 0 };
  let sum = 0,
    mom = 0,
    core = 0,
    top = 0,
    topSum = 0;
  for (let i = l; i <= r; i++) {
    const d = buf[i]! - b;
    if (d <= 0) continue;
    sum += d;
    mom += d * (-R + i * STEP);
    if (d >= 0.75 * mm) core++;
    if (d >= 0.85 * mm) {
      top++;
      topSum += d;
    }
  }
  const coreW = core * STEP;
  // a line wider than a pixel or two shows its own tone across its middle (its plateau, not the
  // shoulders of its edges); a thin one never reaches it
  const v = coreW >= 1.5 ? topSum / top : mm;
  const ink = sum * STEP;
  const off = mom / sum,
    width = ink / v;
  // the pixel each edge passes through (whole, not blended with its neighbours): part-dark where
  // the line was drawn smoothly, all ink or all paper where it was drawn in whole pixels
  let soft = 0;
  for (const e of [off - width / 2, off + width / 2]) {
    const X = Math.floor((x + nx * e) * lk),
      Y = Math.floor((y + ny * e) * lk);
    if (X < 0 || Y < 0 || X >= L.w || Y >= L.h) continue;
    const d = (P - L.data[Y * L.w + X]!) / P - b;
    if (d > 0.15 * v && d < 0.85 * v) soft++;
  }
  return { off, width, v: v + b, ok: !edge, soft };
}

/** The plan to measure on: its lightness (lk: its px per image px) and its paper's tone at a point (image px). */
export interface Plate {
  L: Luma;
  lk: number;
  paperAt: (x: number, y: number) => number;
  buf: Float32Array;
}

export interface Run {
  xs: Float64Array;
  ys: Float64Array;
  ws: number[];
  vs: number[];
  /** Drawn in whole pixels (its edges hard all along): its centres are no finer than those pixels. */
  hard?: boolean;
}

export interface CentreOptions {
  /** How far the rough path may stray from a straight run or arc (px): its turns sharper than this are corners. */
  roughTol: number;
  /** Points left unmeasured at an open path's ends (a junction's clump). */
  skipEnds: number;
  /** How far across to measure (px), and once more, wider, where that finds no clean line. */
  R: number;
  Rwide: number;
  /** A centre farther than this from the rough path is another line's. */
  maxOff: number;
}

/** A turn of the rough path this sharp is a corner. */
const CORNER = Math.cos((25 * Math.PI) / 180);

/**
 * The line's centres along a rough path (px, py: image px; a loop's last point is its first),
 * in runs parted at its corners. A centre where the ink is far wider than the line (another line
 * meeting it) is left out.
 */
export function centresAlong(px: Float64Array, py: Float64Array, loop: boolean, plate: Plate, o: CentreOptions): Run[] {
  const n = px.length;
  const m0 = loop ? n - 1 : n;
  if (m0 < 3) return [];
  let rough = cutIntoPieces(px, py, 0, m0 - 1, o.roughTol);
  if (loop && rough.length > 1) {
    // a loop is begun half-way along its longest straight stretch (never at a corner, where it was
    // first come upon: its two ends meet there like any other two pieces)
    let best = rough[0]!;
    for (const r of rough) if (r.i1 - r.i0 > best.i1 - best.i0) best = r;
    const s0 = (best.i0 + best.i1) >> 1;
    const qx = new Float64Array(n),
      qy = new Float64Array(n);
    for (let i = 0; i < m0; i++) {
      qx[i] = px[(s0 + i) % m0]!;
      qy[i] = py[(s0 + i) % m0]!;
    }
    qx[m0] = qx[0]!;
    qy[m0] = qy[0]!;
    px = qx;
    py = qy;
    rough = cutIntoPieces(px, py, 0, m0 - 1, o.roughTol);
  }
  const tangent = (f: Fit, i: number): { x: number; y: number } => {
    if (f.t === 'line') return { x: f.ux, y: f.uy };
    const a = Math.atan2(py[i]! - f.cy, px[i]! - f.cx);
    return { x: -Math.sin(a) * f.dir, y: Math.cos(a) * f.dir };
  };
  // the corners: where one rough piece turns sharply into the next — and the path itself turns
  // there, within a few pixels (two straight runs cut from a curve meet at an angle too, but the
  // curve turns no more there than anywhere along it)
  const within = (i: number, d: number) => Math.max(0, Math.min(m0 - 1, i + d));
  const way = (i: number, j: number) => {
    const dx = px[j]! - px[i]!,
      dy = py[j]! - py[i]!;
    const l = Math.hypot(dx, dy) || 1;
    return { x: dx / l, y: dy / l };
  };
  const corners: { x: number; y: number; at: number }[] = [];
  for (let j = 0; j + 1 < rough.length; j++) {
    const a = rough[j]!,
      b = rough[j + 1]!;
    const ta = tangent(a.fit, a.i1),
      tb = tangent(b.fit, b.i0);
    if (ta.x * tb.x + ta.y * tb.y >= CORNER) continue;
    const i = a.i1;
    const before = way(within(i, -8), i),
      after = way(i, within(i, 8));
    if (before.x * after.x + before.y * after.y >= CORNER) continue;
    corners.push({ x: px[i]!, y: py[i]!, at: j + 1 });
  }
  // every point measured, with the run it falls in (runs part at the corners)
  const mx: number[] = [],
    my: number[] = [],
    mw: number[] = [],
    mv: number[] = [],
    ms: number[] = [],
    mrun: number[] = [],
    mnear: number[] = [];
  let run = 0,
    ci = 0;
  for (let j = 0; j < rough.length; j++) {
    if (ci < corners.length && corners[ci]!.at === j) {
      run++;
      ci++;
    }
    const rp = rough[j]!;
    const from = rp.i0 === 0 && !loop ? o.skipEnds : rp.i0,
      to = rp.i1 === m0 - 1 && !loop ? m0 - 1 - o.skipEnds : rp.i1;
    for (let i = from; i <= to; i++) {
      if (i > rp.i0 && i === rp.i1 && j < rough.length - 1) continue; // (the next piece's first point)
      const t = tangent(rp.fit, i);
      const nx = -t.y,
        ny = t.x;
      // (narrow first: most lines are measured whole within a few pixels, and one found whole in a
      // narrow window is found just the same in a wider one)
      let a = across(plate.L, plate.lk, plate.paperAt, px[i]!, py[i]!, nx, ny, Math.min(3, o.R), plate.buf);
      if (!a.ok && o.R > 3) a = across(plate.L, plate.lk, plate.paperAt, px[i]!, py[i]!, nx, ny, o.R, plate.buf);
      if (!a.ok && o.Rwide > o.R) a = across(plate.L, plate.lk, plate.paperAt, px[i]!, py[i]!, nx, ny, o.Rwide, plate.buf);
      if (!a.ok || Math.abs(a.off) > o.maxOff) continue;
      let near = Infinity;
      for (const c of corners) near = Math.min(near, Math.hypot(px[i]! - c.x, py[i]! - c.y));
      mx.push(px[i]! + nx * a.off);
      my.push(py[i]! + ny * a.off);
      mw.push(a.width);
      mv.push(a.v);
      ms.push(a.soft);
      mrun.push(run);
      mnear.push(near);
    }
  }
  if (!mx.length) return [];
  // (a point far wider than the line is where another line meets it: not the line's own centre; and
  // nothing within the line's reach of a corner counts: the ink there is both arms')
  const wm = median(mw);
  const guard = wm / 2 + 1.5;
  const out: Run[] = [];
  const ids: number[] = [];
  let at = 0;
  while (at < mx.length) {
    const r = mrun[at]!;
    const keep: number[] = [];
    for (; at < mx.length && mrun[at] === r; at++) if (mw[at]! <= 1.6 * wm + 0.8 && mnear[at]! >= guard) keep.push(at);
    if (keep.length < 2) continue;
    let soft = 0;
    for (const i of keep) soft += ms[i]!;
    out.push({ xs: Float64Array.from(keep, (i) => mx[i]!), ys: Float64Array.from(keep, (i) => my[i]!), ws: keep.map((i) => mw[i]!), vs: keep.map((i) => mv[i]!), hard: soft < 0.2 * keep.length });
    ids.push(r);
  }
  // (round a loop, the stretch it was begun in is one run: its last part and its first)
  if (loop && out.length > 1 && ids[0] === 0 && ids[ids.length - 1] === run) {
    const a = out.pop()!,
      b = out[0]!;
    out[0] = { xs: concat(a.xs, b.xs), ys: concat(a.ys, b.ys), ws: [...a.ws, ...b.ws], vs: [...a.vs, ...b.vs], hard: !!(a.hard && b.hard) };
  }
  return out;
}

function concat(a: Float64Array, b: Float64Array): Float64Array {
  const o = new Float64Array(a.length + b.length);
  o.set(a);
  o.set(b, a.length);
  return o;
}

export interface FitPiece {
  fit: Fit;
  run: number;
  xs: Float64Array;
  ys: Float64Array;
  ws: number[];
  vs: number[];
  /** Where it starts and ends: its own first and last centres on it, then met exactly with the pieces either side. */
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

/** The point of a fit nearest (x, y). */
export function onFit(f: Fit, x: number, y: number): { x: number; y: number } {
  if (f.t === 'line') {
    const s = (x - f.cx) * f.ux + (y - f.cy) * f.uy;
    return { x: f.cx + f.ux * s, y: f.cy + f.uy * s };
  }
  const a = Math.atan2(y - f.cy, x - f.cx);
  return { x: f.cx + f.r * Math.cos(a), y: f.cy + f.r * Math.sin(a) };
}

/**
 * The runs' centres cut into lines and arcs within `tol` (a run's own width may ask for more), and
 * each piece's ends met with the next's: a line meets a line at their crossing (at a corner, or a
 * bend within a run); otherwise each end goes as near half-way between the two as its own line or
 * arc goes.
 */
export function piecesOf(runs: Run[], tolFor: (w: number) => number, loop = false): FitPiece[] {
  const out: FitPiece[] = [];
  runs.forEach((r, ri) => {
    const ws = r.ws.slice().sort((p, q) => p - q);
    // (never finer than the centres can be: a line drawn in whole pixels steps up to half a pixel
    // or more from its true course, and is fitted through its steps, not along them)
    const tol = Math.max(tolFor(ws[ws.length >> 1]!), r.hard ? 0.7 : 0);
    for (const pc of cutIntoPieces(r.xs, r.ys, 0, r.xs.length - 1, tol)) {
      const a = onFit(pc.fit, r.xs[pc.i0]!, r.ys[pc.i0]!),
        b = onFit(pc.fit, r.xs[pc.i1]!, r.ys[pc.i1]!);
      out.push({ fit: pc.fit, run: ri, xs: r.xs.slice(pc.i0, pc.i1 + 1), ys: r.ys.slice(pc.i0, pc.i1 + 1), ws: r.ws.slice(pc.i0, pc.i1 + 1), vs: r.vs.slice(pc.i0, pc.i1 + 1), ax: a.x, ay: a.y, bx: b.x, by: b.y });
    }
  });
  const wOf = out.map((pc) => median(pc.ws));
  // (round a loop, its last piece meets its first too)
  const joints = out.length - 1 + (loop && out.length > 1 ? 1 : 0);
  for (let jj = 0; jj < joints; jj++) {
    const j = jj % out.length,
      j2 = (jj + 1) % out.length;
    const p = out[j]!,
      q = out[j2]!;
    const corner = p.run !== q.run;
    const w = Math.max(wOf[j]!, wOf[j2]!);
    let x = (p.bx + q.ax) / 2,
      y = (p.by + q.ay) / 2;
    if (p.fit.t === 'line' && q.fit.t === 'line') {
      const cr = p.fit.ux * q.fit.uy - p.fit.uy * q.fit.ux;
      if (Math.abs(cr) > Math.sin((2 * Math.PI) / 180)) {
        const t = ((q.ax - p.bx) * q.fit.uy - (q.ay - p.by) * q.fit.ux) / cr;
        const cx = p.bx + p.fit.ux * t,
          cy = p.by + p.fit.uy * t;
        const reach = corner ? 3 * w + 4 : 2;
        if (Math.hypot(cx - p.bx, cy - p.by) <= reach && Math.hypot(cx - q.ax, cy - q.ay) <= reach) {
          x = cx;
          y = cy;
        }
      }
    }
    // (each end only slides along its own line or arc: a piece keeps the line it was fitted to)
    if (!corner || Math.hypot(p.bx - q.ax, p.by - q.ay) <= 3 * w + 4) {
      const pb = onFit(p.fit, x, y),
        qa = onFit(q.fit, x, y);
      p.bx = pb.x;
      p.by = pb.y;
      q.ax = qa.x;
      q.ay = qa.y;
    }
  }
  return out;
}

function median(a: number[]): number {
  if (!a.length) return 0;
  const s = a.slice().sort((p, q) => p - q);
  return s[s.length >> 1]!;
}
